import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@/app/logger';
import { buildPaginatedArticleListUrl } from '../../../shared/lib/buildPaginatedArticleListUrl';
import { partitionArticlePage } from '../../../shared/lib/partitionArticlePage';
import { fetchPublishedArticles } from '../lib/articlesPublicApi';
import { CONTENT_LIST_PAGE_COPY } from '../lib/contentListPageConstants';
import type { InfiniteListPatternProps } from '../lib/contentListPageTypes';
import {
  INITIAL_INFINITE_LIST_PATTERN_STATE,
  infiniteListPatternStateAppendPage,
  infiniteListPatternStateError,
  infiniteListPatternStateFromPage,
  infiniteListPatternStateLoadMoreError,
  infiniteListPatternStateLoading,
  infiniteListPatternStateLoadingMore,
  type InfiniteListPatternState,
} from '../lib/infiniteListPatternState';

const log = logger.create('useInfiniteListPatternItems');

function resolveUserErrorMessage(
  err: unknown,
  overrideMessage?: string,
): string {
  if (overrideMessage) return overrideMessage;
  if (err instanceof Error && err.message) return err.message;
  return CONTENT_LIST_PAGE_COPY.defaultError;
}

export interface InfiniteListPatternResult {
  state: InfiniteListPatternState;
  loadMore: () => void;
}

export function useInfiniteListPatternItems(
  listProps: InfiniteListPatternProps | undefined,
  pageTitle: string,
): InfiniteListPatternResult {
  const [state, setState] = useState<InfiniteListPatternState>(
    INITIAL_INFINITE_LIST_PATTERN_STATE,
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const filterFnRef = useRef(listProps?.filterFn);
  const errorMessageRef = useRef(listProps?.errorMessage);
  const offsetRef = useRef(0);
  const inFlightRef = useRef(false);

  filterFnRef.current = listProps?.filterFn;
  errorMessageRef.current = listProps?.errorMessage;

  const articleType = listProps?.articleType;
  const pageSize = listProps?.pageSize;

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      if (!articleType || !pageSize) {
        return;
      }

      const fetchUrl = buildPaginatedArticleListUrl({
        type: articleType,
        pageSize,
        offset,
      });

      log.info('fetchPage', 'Fetching article page', { fetchUrl, pageTitle, offset, append });

      const raw = await fetchPublishedArticles(fetchUrl);
      const filtered = filterFnRef.current ? filterFnRef.current(raw) : raw;
      const { items, hasMore } = partitionArticlePage(filtered, pageSize);

      log.info('fetchPage', 'Article page fetched', {
        fetchUrl,
        pageTitle,
        offset,
        count: items.length,
        hasMore,
      });

      return { items, hasMore };
    },
    [articleType, pageSize, pageTitle],
  );

  useEffect(() => {
    if (!articleType || !pageSize) {
      setState(INITIAL_INFINITE_LIST_PATTERN_STATE);
      return;
    }

    let cancelled = false;
    offsetRef.current = 0;
    inFlightRef.current = true;
    setState(infiniteListPatternStateLoading());

    fetchPage(0, false)
      .then((page) => {
        if (cancelled || !page) return;
        offsetRef.current = page.items.length;
        setState(infiniteListPatternStateFromPage(page.items, page.hasMore));
      })
      .catch((err) => {
        if (cancelled) return;
        const message = resolveUserErrorMessage(err, errorMessageRef.current);
        log.error('fetchPage', 'Initial page fetch failed', { pageTitle, message });
        setState(infiniteListPatternStateError(message));
      })
      .finally(() => {
        inFlightRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [articleType, pageSize, fetchPage, pageTitle]);

  const loadMore = useCallback(() => {
    if (!articleType || !pageSize || inFlightRef.current) {
      return;
    }

    const prev = stateRef.current;
    if (prev.status !== 'success' || !prev.hasMore) {
      return;
    }

    inFlightRef.current = true;
    setState(infiniteListPatternStateLoadingMore(prev));
    const offset = offsetRef.current;

    fetchPage(offset, true)
      .then((page) => {
        if (!page) return;
        offsetRef.current += page.items.length;
        setState((prev) => infiniteListPatternStateAppendPage(prev, page.items, page.hasMore));
      })
      .catch((err) => {
        const message = resolveUserErrorMessage(err, errorMessageRef.current);
        log.error('loadMore', 'Load more failed', { pageTitle, offset, message });
        setState((prev) => infiniteListPatternStateLoadMoreError(prev, message));
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [articleType, pageSize, fetchPage, pageTitle]);

  return { state, loadMore };
}
